import { createContext, useContext, useState } from 'react';
import { useProperty } from '@eti/providers';
import useSaveTrips from '../../../../../common/custom-hooks/save-trips/useSaveTrips';
import { useToggle } from '../../../../../common/custom-hooks/toggle';
import type {
  ResultPageProviderProps,
  ResultPageContextInterface,
  TripDetailsType,
  SelectedTripType,
} from './model';

export const ResultPageContext = createContext<ResultPageContextInterface | null>(null);

const ResultPageContextProvider = ({
  addTrip,
  addTripForSelectedTrip,
  closeFiltersPanel,
  handleSearch,
  handleSearchData,
  handleShowValidWithVoucher,
  handleSort,
  isInitialTripSelectionLoading,
  children,
  isFiltersPanelOpen,
  isSearchLoading,
  isSelectTripLoading,
  isSelfServiceRebooking,
  onShowAncillaryBundleModal,
  rebookingData,
  redirectToDBF,
  redirectToTravelerDetails,
  searchData,
  selectedTripData,
  setIsSearchLoading,
  setShouldRefetch,
  setShowTripIsNotAvailableModal,
  shouldRefetch,
  shouldShowSelectedTrip,
  shouldShowTripIsNotAvailableModal,
  toggleFiltersPanel,
}: ResultPageProviderProps) => {
  const { p } = useProperty();
  const [selectedFlightId, setSelectedFlightId] = useState<number | string | null>(null);
  const [selectedTrip, setSelectedTrip] = useState<SelectedTripType>(null);
  const [tripsDetails, setTripsDetails] = useState<TripDetailsType>(null);
  const { savedTrips, addSavedTrip, removeSavedTrip } = useSaveTrips();
  const [isBaggagePopupActive, setIsBaggagePopupActive] = useState(false);
  const {
    isOpen: isSearchPanelOpen,
    handleClose: closeSearchPanel,
    handleToggle: toggleSearchPanel,
  } = useToggle(p('Result.SearchForm.Expanded.Enabled'));

  const selectedAndSearchTripResult =
    selectedTrip && tripsDetails ? [selectedTrip, ...tripsDetails] : tripsDetails;

  const flight = selectedAndSearchTripResult?.find((item) => {
    return item?.id === selectedFlightId;
  });

  const selectedFlightDetails = {
    id: selectedFlightId,
    flight,
  };

  const shouldShowSkeletonOnResultPage = Boolean(!searchData || isInitialTripSelectionLoading);

  const values: ResultPageContextInterface = {
    addSavedTrip,
    addTrip,
    addTripForSelectedTrip,
    closeSearchPanel,
    closeFiltersPanel,
    handleSearch,
    handleSearchData,
    handleShowValidWithVoucher,
    handleSort,
    isBaggagePopupActive,
    isFiltersPanelOpen,
    isInitialTripSelectionLoading,
    isSearchLoading,
    isSearchPanelOpen,
    isSelectTripLoading,
    isSelfServiceRebooking,
    onShowAncillaryBundleModal,
    rebookingData,
    redirectToDBF,
    redirectToTravelerDetails,
    removeSavedTrip,
    savedTrips,
    searchData,
    selectedFlightDetails,
    selectedTrip,
    selectedTripData,
    setIsBaggagePopupActive,
    setIsSearchLoading,
    setShouldRefetch,
    setShowTripIsNotAvailableModal,
    setSelectedFlightId,
    setSelectedTrip,
    setTripsDetails,
    shouldRefetch,
    shouldShowSelectedTrip,
    shouldShowSkeletonOnResultPage,
    shouldShowTripIsNotAvailableModal,
    toggleFiltersPanel,
    toggleSearchPanel,
    tripsDetails,
  };

  return <ResultPageContext.Provider value={{ ...values }}>{children}</ResultPageContext.Provider>;
};

const useResultPageContext = () => {
  const context = useContext(ResultPageContext);
  if (!context) {
    throw new Error('useResultPageContext must be used inside ResultPageContextProvider');
  }
  return context;
};

export { ResultPageContextProvider, useResultPageContext };
